/**
 * GENERATED by /docs-writer from docs/connection-audit.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/connection-audit.md`.
 *
 * source-sha: 319fe601eeccadd324a4632473c369f57fbef3c8
 * sources (from the markdown's "Sources" section):
 *   - apps/web/src/pages/VpnConnectionAuditPage.tsx (columns, filters, time-range picker, paging, link state)
 *   - apps/api/src/app/vpn/connection-audit.service.ts, pinned by apps/api/test/vpn-connection-audit.spec.ts
 *   - apps/api/src/app/vpn/vpn.resolver.ts (@RequireRole('root')), apps/web/src/App.tsx, apps/web/src/components/AppShell.tsx, packages/shared-types/src/rbac.ts, e2e/api/rbac.spec.ts
 *   - wg-orchestrator internal/audit/audit.go (what is recorded, ports, result values, when a row is written)
 *   - CLAUDE.md (VPN Network views), apps/api/src/app/vpn/vpn.tokens.ts
 *   - The path in the diagram: wg-orchestrator deploy/nftables.conf (forwarding), internal/audit/audit.go and internal/audit/store_postgres.go (look-up, write at the end); the read on each search in apps/api/src/app/vpn/vpn.resolver.ts, apps/api/src/app/vpn/connection-audit.service.ts and apps/web/src/pages/VpnConnectionAuditPage.tsx; no reader under apps/scheduler
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'what-the-page-shows', label: 'What the page shows' },
  { id: 'who-can-open-it', label: 'Who can open it' },
  { id: 'how-far-back-it-goes', label: 'How far back it goes' },
  { id: 'how-to-search-it', label: 'How to search it' },
  { id: 'where-the-records-come-from', label: 'Where the records come from' },
];

export function ConnectionAuditDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Network · Reference</p>
        <h1 className="doc-title">Connection audit</h1>
        <p className="doc-lede">
          <Link to="/vpn/connection-audit">Network → Connection audit</Link> lists every connection a staff laptop
          made to a signage device over <strong>ssh, adb or vnc</strong> through the VPN gateway. Only the{' '}
          <strong>root</strong> role can open it, and it looks back <strong>24 hours</strong>, or up to{' '}
          <strong>30 days</strong> for one device.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-12">12 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="what-the-page-shows">What the page shows</h2>
        <p className="doc-p">
          The page shows one row for each finished connection, newest first. The VPN gateway writes the row when
          the connection <strong>ends</strong>, so a session that is still open is not listed yet.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Column</th>
                <th>What it shows</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>When</strong></td>
                <td>The time the connection ended, in your browser&rsquo;s time zone.</td>
              </tr>
              <tr>
                <td><strong>Person</strong></td>
                <td>
                  The email and name of the laptop&rsquo;s owner. It reads <code>unattributed</code> when the
                  gateway found no peer for the laptop&rsquo;s address.
                </td>
              </tr>
              <tr>
                <td><strong>Device</strong></td>
                <td>
                  The device&rsquo;s name and UUID. It shows the device&rsquo;s VPN address when the gateway found
                  no device for that address.
                </td>
              </tr>
              <tr>
                <td><strong>Service</strong></td>
                <td>
                  <code>ssh</code> for port 22 or 3000, <code>adb</code> for port 5555, <code>vnc</code> for port
                  5900.
                </td>
              </tr>
              <tr>
                <td><strong>Result</strong></td>
                <td>
                  <code>established</code> when the device answered. <code>no-reply</code> when it did not, which
                  means a blocked or failed attempt.
                </td>
              </tr>
              <tr>
                <td><strong>Region</strong></td>
                <td>The gateway region that recorded the connection.</td>
              </tr>
              <tr>
                <td><strong>Duration</strong></td>
                <td>
                  How long the connection lasted. It reads <code>0ms</code> when the gateway has no start time
                  for the session.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The gateway looks up the person and the device as a connection starts, and keeps the start time for one
          hour. A check every 5 minutes drops start times older than that. A session that ends after its start
          time was dropped is still written, with a <strong>Duration</strong> of <code>0ms</code> and with the
          person and the device looked up at the end instead. A peer removed before such a session ends can read{' '}
          <code>unattributed</code>.
        </p>
        <p className="doc-p">
          <strong>Details</strong> on a row opens the full record. It adds the protocol, the laptop&rsquo;s VPN
          address and subnet, the device&rsquo;s VPN address, port and subnet, and the number of bytes.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            The gateway records these four ports only, and only from a person subnet to the device subnet. A
            connection to a device on any other port leaves no row.{' '}
            <Link to="/docs/vpn-subnets">VPN subnets</Link> lists which subnet may reach which port.
          </p>
        </aside>

        <h2 className="doc-h2" id="who-can-open-it">Who can open it</h2>
        <p className="doc-p">
          Only people with the <strong>root</strong> role in this dashboard can open the page. The roles{' '}
          <strong>maintainer</strong>, <strong>engineer</strong> and <strong>new member</strong> cannot. The log
          is the SOC2 audit record of staff access to devices, which is why it is limited to root.
        </p>
        <ul className="doc-list">
          <li>
            <strong>No sidebar entry.</strong> Without root, Network shows Peers and Subnets only.
          </li>
          <li>
            <strong>No page.</strong> The address <code>/vpn/connection-audit</code> sends you to Docs.
          </li>
          <li>
            <strong>No data.</strong> The API refuses the query for every role except root.
          </li>
          <li>
            <strong>Not on Activity either.</strong> The Activity page leaves these records out, because people
            without root can open it.
          </li>
        </ul>
        <p className="doc-p">
          If you need a look-up and you are not root, ask a root user to run the search. Root users assign roles
          on Administration → Users &amp; roles.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Root here is your role in this dashboard. It is separate from the staff-app role that decides your
            VPN subnet, even though both have a role called root.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-far-back-it-goes">How far back it goes</h2>
        <p className="doc-p">
          The page reads the last <strong>24 hours</strong> unless you name a device. With a device UUID in the
          filter, you can pick the last <strong>7 days</strong> or the last <strong>30 days</strong>. No search
          reaches further back than 30 days.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Your search</th>
                <th>Time ranges you can pick</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>No device UUID</strong></td>
                <td>Last 24 hours</td>
              </tr>
              <tr>
                <td><strong>One device UUID</strong></td>
                <td>Last 24 hours, last 7 days or last 30 days</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The API applies the same limits, so editing the page address does not widen a search.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            The log grows by about 57,000 rows a day. A search for one device uses an index and stays under a
            millisecond over 30 days. A 30-day search without a device reads every row in the range and took 2.2
            seconds on a database other services share. Both were measured on 2026-09-15.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-to-search-it">How to search it</h2>
        <p className="doc-p">
          Set the filters, then press <strong>Apply</strong>. <strong>Reset</strong> clears them and returns to
          the last 24 hours.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Filter</th>
                <th>What it matches</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Person</strong></td>
                <td>Any part of the email address. Upper and lower case match alike.</td>
              </tr>
              <tr>
                <td><strong>Device UUID</strong></td>
                <td>The whole UUID, exactly. This filter also unlocks the 7-day and 30-day ranges.</td>
              </tr>
              <tr>
                <td><strong>Service</strong></td>
                <td>
                  <code>ssh</code>, <code>adb</code> or <code>vnc</code>.
                </td>
              </tr>
              <tr>
                <td><strong>Result</strong></td>
                <td>
                  <code>established</code> or <code>no-reply</code>.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul className="doc-list">
          <li>
            <strong>Results come 100 to a page, newest first.</strong> The page shows no total. Press{' '}
            <strong>Next</strong> until the line under the table ends with <em>(end)</em>.
          </li>
          <li>
            <strong>A search is a link.</strong> The page address holds the applied filters and the page number.
            Send it to another root user and they see the same search.
          </li>
        </ul>

        <h2 className="doc-h2" id="where-the-records-come-from">Where the records come from</h2>
        <p className="doc-p">
          wg-orchestrator records each connection at the VPN gateway. The dashboard reads the records from the
          audit database.
        </p>
        <DocSequence
          caption="From a connection to a row on the page"
          participants={[
            { id: 'Staff', label: 'Staff member', kind: 'person' },
            { id: 'Root', label: 'Root user', kind: 'person' },
            { id: 'Gw', label: 'VPN gateway' },
            { id: 'Dev', label: 'Signage device' },
            { id: 'Db', label: 'Audit database' },
            { id: 'Dash', label: 'Dashboard' },
          ]}
          steps={[
            { from: 'Staff', to: 'Gw', text: 'Connect over ssh, adb or vnc' },
            { from: 'Gw', to: 'Dev', text: 'Forward it if allowed' },
            { from: 'Gw', to: 'Gw', text: 'Look up person and device' },
            { kind: 'note', over: ['Gw', 'Dev'], text: 'No row exists while the session is open.' },
            { from: 'Gw', to: 'Gw', text: 'See the connection end' },
            { from: 'Gw', to: 'Db', text: 'Write one row' },
            {
              kind: 'note',
              over: ['Db', 'Dash'],
              text: 'Nothing is collected on a schedule. Each search reads the database directly.',
            },
            { from: 'Root', to: 'Dash', text: 'Open the page or press Apply' },
            { from: 'Dash', to: 'Db', text: 'Read rows in the time range' },
            { kind: 'reply', from: 'Db', to: 'Dash', text: 'Rows, newest first' },
            { kind: 'reply', from: 'Dash', to: 'Root', text: 'Show 100 rows a page' },
          ]}
        />
        <ul className="doc-list">
          <li>
            <strong>A row can be missing.</strong> The gateway tries each write once, for up to 5 seconds, and
            does not retry a failed one, so a missing row does not prove that no connection happened.
          </li>
          <li>
            <strong>The dashboard only reads.</strong> It has no action that changes or deletes a record.
          </li>
          <li>
            <strong>Some environments have no log.</strong> Where no audit database is connected, the page shows{' '}
            <em>Couldn&rsquo;t load the audit log</em>. Ask in #devops-infra if you see that in production.
          </li>
        </ul>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Need to know who reached a device?</h2>
            <p>Open the page and filter by the device UUID to look back up to 30 days.</p>
          </div>
          <Link className="btn" to="/vpn/connection-audit">
            Open Connection audit
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
